/**
 * lx-digest-view —— 文件整理结果（覆盖层）
 *
 * 展示一份 file_digests 记录：摘要文字 + 文件清单。
 * 这是「整合消息记录中的文件」这个需求最终让用户看到的东西 ——
 * 一份可复看、可复用的存档，而不是一次性的 AI 回复。
 *
 * 摘要可能是空的（AI 那一步失败或还没跑）—— 这时显示一句诚实的说明，
 * 而不是空白。清单部分永远可用，因为它是本地收集的。
 */

import { LxElement, define } from '@/core/base-element';
import { filesRepo, formatSize, categorize } from '@/data/repo.files';
import { popOverlay } from '@/core/ui-store';
import type { FileDigest } from '@/types/models';

const TYPE_ICON: Record<string, string> = {
  image: '🖼',
  doc: '📄',
  sheet: '📊',
  pdf: '📕',
  voice: '🎤',
  video: '🎬',
  archive: '🗜',
  other: '📎',
};

const TYPE_LABEL: Record<string, string> = {
  image: '图片',
  doc: '文档',
  sheet: '表格',
  pdf: 'PDF',
  voice: '语音',
  video: '视频',
  archive: '压缩包',
  other: '其他',
};

export class LxDigestView extends LxElement {
  private _digestId = '';
  private _digest: FileDigest | null = null;
  private _loading = true;

  set digestId(value: string) {
    this._digestId = value;
    void this.load();
  }

  get digestId(): string {
    return this._digestId;
  }

  private async load(): Promise<void> {
    this._loading = true;
    this.update();
    this._digest = (await filesRepo.getDigest(this._digestId)) ?? null;
    this._loading = false;
    this.update();
  }

  protected styles(): string {
    return `
      :host {
        position: absolute;
        inset: 0;
        z-index: var(--z-overlay);
        display: flex;
        flex-direction: column;
        background: var(--c-bg);
        animation: slide-in var(--dur-base) var(--ease-standard);
      }
      @keyframes slide-in {
        from { transform: translateX(6%); opacity: .4; }
        to { transform: translateX(0); opacity: 1; }
      }

      header {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        gap: var(--sp-3);
        padding: var(--sp-3) var(--layout-content-pad-x);
        padding-top: calc(var(--sp-3) + env(safe-area-inset-top, 0px));
        background: var(--c-surface);
        border-bottom: 1px solid var(--c-border);
      }
      .back {
        width: 34px; height: 34px;
        display: grid; place-items: center;
        border-radius: var(--radius-full);
        color: var(--c-fg);
        flex-shrink: 0;
      }
      .back:hover { background: var(--c-surface-2); }
      .back svg { width: 20px; height: 20px; fill: none; stroke: currentColor; stroke-width: 1.8; }
      .title {
        flex: 1; min-width: 0;
        font-size: var(--fs-lg);
        font-weight: var(--fw-medium);
        color: var(--c-fg);
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      }

      .body {
        flex: 1; min-height: 0;
        overflow-y: auto;
        overscroll-behavior: contain;
        padding: var(--sp-4) var(--layout-content-pad-x);
        display: flex; flex-direction: column; gap: var(--sp-4);
      }

      .summary-card {
        background: var(--c-surface);
        border: 1px solid var(--c-border);
        border-left: 3px solid var(--c-primary);
        border-radius: var(--radius-md);
        padding: var(--sp-3) var(--sp-4);
      }
      .summary-label {
        font-size: var(--fs-xs);
        color: var(--c-fg-subtle);
        margin-bottom: var(--sp-2);
        letter-spacing: .05em;
      }
      .summary-text {
        font-size: var(--fs-md);
        line-height: var(--lh-relaxed, 1.7);
        color: var(--c-fg);
        white-space: pre-wrap;
      }
      .summary-text.empty { color: var(--c-fg-subtle); font-style: italic; }

      .stats {
        display: flex; flex-wrap: wrap; gap: var(--sp-2);
      }
      .chip {
        font-size: var(--fs-xs);
        padding: 3px var(--sp-2);
        border-radius: var(--radius-full);
        background: var(--c-surface-2);
        color: var(--c-fg-muted);
      }

      .list-title {
        font-size: var(--fs-sm);
        color: var(--c-fg-subtle);
        margin-bottom: var(--sp-1);
      }

      ul { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: var(--sp-1); }

      li {
        display: flex; align-items: center; gap: var(--sp-3);
        padding: var(--sp-3);
        background: var(--c-surface);
        border: 1px solid var(--c-border);
        border-radius: var(--radius-sm);
      }
      .icon { font-size: var(--fs-lg); flex-shrink: 0; }
      .info { flex: 1; min-width: 0; }
      .fname {
        font-size: var(--fs-md); color: var(--c-fg);
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      }
      .meta { font-size: var(--fs-xs); color: var(--c-fg-subtle); margin-top: 2px; }

      .empty-note, .loading {
        margin: auto; text-align: center;
        color: var(--c-fg-subtle);
        font-size: var(--fs-sm);
      }
    `;
  }

  protected render(): string {
    if (this._loading) {
      return `
        <header>
          <button class="back" data-back aria-label="返回">${BACK_ICON}</button>
          <div class="title">文件整理</div>
        </header>
        <div class="body"><div class="loading">读取中…</div></div>
      `;
    }

    if (!this._digest) {
      return `
        <header>
          <button class="back" data-back aria-label="返回">${BACK_ICON}</button>
          <div class="title">文件整理</div>
        </header>
        <div class="body"><div class="empty-note">这份整理记录已经不存在了。</div></div>
      `;
    }

    const d = this._digest;
    const chips = [
      `${d.itemCount} 个文件`,
      formatSize(d.totalSize),
      ...Object.entries(d.byType).map(([k, v]) => `${TYPE_LABEL[k] ?? k} ${v}`),
    ];

    const summaryHtml = d.summary
      ? `<div class="summary-text">${this.esc(d.summary)}</div>`
      : `<div class="summary-text empty">AI 摘要还没生成（文件清单已经存好了）。</div>`;

    const items = d.items
      .map((it) => {
        const cat = categorize(it.mime);
        const when = it.createdAt
          ? new Date(it.createdAt).toLocaleString('zh-CN', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })
          : '';
        return `
          <li>
            <span class="icon">${TYPE_ICON[cat] ?? '📎'}</span>
            <div class="info">
              <div class="fname">${this.esc(it.name)}</div>
              <div class="meta">${TYPE_LABEL[cat] ?? '其他'} · ${formatSize(it.size)}${when ? ` · ${when}` : ''}</div>
            </div>
          </li>
        `;
      })
      .join('');

    return `
      <header>
        <button class="back" data-back aria-label="返回">${BACK_ICON}</button>
        <div class="title">${this.esc(d.title)}</div>
      </header>
      <div class="body">
        <div class="summary-card">
          <div class="summary-label">摘要</div>
          ${summaryHtml}
        </div>
        <div class="stats">${chips.map((c) => `<span class="chip">${this.esc(c)}</span>`).join('')}</div>
        <div>
          <div class="list-title">文件清单</div>
          <ul>${items}</ul>
        </div>
      </div>
    `;
  }

  protected onMounted(): void {
    this.$('[data-back]')?.addEventListener('click', () => popOverlay());
  }
}

const BACK_ICON = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 18l-6-6 6-6" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

define('lx-digest-view', LxDigestView);
